import { Color } from "three";

const samples = [ 0xff3800, 0xff5300, 0xff6500, 0xff7300, 0xff7e00, 0xff8912, 0xff932c, 0xff9d3f, 0xffa54f, 0xffad5e, 0xffb46b, 0xffbb78, 0xffc184, 0xffc78f, 0xffcc99, 0xffd1a3, 0xffd5ad, 0xffd9b6, 0xffddbe, 0xffe1c6, 0xffe4ce, 0xffe8d5, 0xffebdc, 0xffeee3, 0xfff0e9, 0xfff3ef, 0xfff5f5, 0xfff8fb, 0xfef9ff, 0xf9f6ff, 0xf5f3ff, 0xf0f1ff, 0xedefff, 0xe9edff, 0xe6ebff, 0xe3e9ff, 0xe0e7ff, 0xdde6ff, 0xdae4ff, 0xd8e3ff, 0xd6e1ff, 0xd3e0ff, 0xd1dfff, 0xcfddff, 0xcedcff, 0xccdbff, 0xcadaff, 0xc9d9ff, 0xc7d8ff, 0xc6d8ff, ];

// Temporaries for lerping
const a = new Color(), b = new Color();

const
    WHITE_POINT = 6500,
    MIN_K = 1000,
    MAX_K = 10800,
    K_STEP = 200,
    K_SPREAD = MAX_K - MIN_K;

/**
 * This converts a blackbody temperature between 1000 and 9600 K to an sRGB
 * color. The values are linearly interpolated from pre-computed samples over
 * the range spaced 200K apart.
 *
 * Note that the 6500 K value is treated specially as pure white.
 *
 * Source: http://www.vendian.org/mncharity/dir3/blackbody/
 *
 *   1000 K  #ff3800            6000 K   #fff3ef
 *   1200 K  #ff5300            6200 K   #fff5f5
 *   1400 K  #ff6500            6400 K   #fff8fb
 *   1600 K  #ff7300            6600 K   #fef9ff
 *   1800 K  #ff7e00            6800 K   #f9f6ff
 *   2000 K  #ff8912            7000 K   #f5f3ff
 *   2200 K  #ff932c            7200 K   #f0f1ff
 *   2400 K  #ff9d3f            7400 K   #edefff
 *   2600 K  #ffa54f            7600 K   #e9edff
 *   2800 K  #ffad5e            7800 K   #e6ebff
 *   3000 K  #ffb46b            8000 K   #e3e9ff
 *   3200 K  #ffbb78            8200 K   #e0e7ff
 *   3400 K  #ffc184            8400 K   #dde6ff
 *   3600 K  #ffc78f            8600 K   #dae4ff
 *   3800 K  #ffcc99            8800 K   #d8e3ff
 *   4000 K  #ffd1a3            9000 K   #d6e1ff
 *   4200 K  #ffd5ad            9200 K   #d3e0ff
 *   4400 K  #ffd9b6            9400 K   #d1dfff
 *   4600 K  #ffddbe            9600 K   #cfddff
 *   4800 K  #ffe1c6            9800 K   #cedcff
 *   5000 K  #ffe4ce            10000 K  #ccdbff
 *   5200 K  #ffe8d5            10200 K  #cadaff
 *   5400 K  #ffebdc            10400 K  #c9d9ff
 *   5600 K  #ffeee3            10600 K  #c7d8ff
 *   5800 K  #fff0e9            10800 K  #c6d8ff
 */
export const blackbody = (K: number, result?: Color): Color => {
    if(!result) result = new Color();

    // The sRGB white point of 6500 K is special. It's not covered by our
    // samples exactly, so we return pure white.
    if(K == WHITE_POINT) {
        result.set(0xFFFFFF);
        return result;
    };

    // Clamp the input to the range [1000, 10800]
    K = Math.max(MIN_K, Math.min(MAX_K, K));

    const
        i = (samples.length - 1) * ((K - MIN_K) / K_SPREAD),
        j = Math.floor(i),
        k = Math.ceil(i);

    if(j == k) {
        result.set(samples[j]);
        return result;
    } else {
        const alpha = (K % K_STEP) / K_STEP;
        a.set(samples[j]);
        b.set(samples[k]);

        result.lerpColors(a, b, alpha);
        return result;
    }
}
export default blackbody;

/**
 * @param stops the number of regularly spaced points to sample for the
 * gradient. Note that a special stop is always placed for the white point, so
 * usually you will get `stops + 1` elements back (unless one of the existing
 * stops happens to hit the white point exactly).
 */
export const blackbodyCssGradient = (): string => {
    const
        stops = [MIN_K, 1100, 1400, 2800, 3200, 5500, 6200, WHITE_POINT, 8000, 9600, MAX_K],
        color = new Color(),
        result: string[] = [];

    for(const K of stops) {
        const percent = 100 * (K - MIN_K) / K_SPREAD;

        blackbody(K, color);
        result.push(
            `#${color.getHexString()} ${percent.toFixed(1)}%`);

        if(K == WHITE_POINT) {
            const percent = 100 * (WHITE_POINT - MIN_K) / K_SPREAD;
            result.push(`#ffffff ${percent.toFixed(1)}%`);
        }
    }

    return result.join(', ');
};
